import React, { useState, useMemo } from "react";
import ReportViewer from "./ReportViewerComponent";
import { Checkbox } from "@progress/kendo-react-inputs";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { ComboBox, MultiSelect } from "@progress/kendo-react-dropdowns";
import Icon from "components/Global/icon";
import styles from "./Reports.module.scss";
import { SectionDividerFull } from "components/Contact/sectionDivider";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import { ReportURL } from "api/axios.config";
import { formatDateForAPI } from "components/common/formatter";
import { useCompany } from "services/companyService";
import { useActiveDepartments } from "services/departmentService";
import { useActiveWorkTypes } from "services/workTypeService";
import { useActiveFeeEarner } from "services/userService";
import pcRenderedIcon from "assets/pc-rendered.svg";
import pcReceivedIcon from "assets/pc-received.svg";
import agedDebtorsIcon from "assets/aged-debator.svg";
import dayBookIcon from "assets/day-book.svg";

const BillingReport = () => {

  const reportsData = [
    {
      icon: dayBookIcon,
      label: "Day Book",
      value: "BillsDayBook.trdp",
    },
    {
      icon: agedDebtorsIcon,
      label: "Aged Debtors",
      value: "AgedDebtors.trdp",
    },
    {
      icon: pcRenderedIcon,
      label: "P/C Rendered",
      value: "ProfitCostRendered.trdp",
    },
    {
      icon: pcReceivedIcon,
      label: "P/C Received",
      value: "ProfitCostReceived.trdp",
    },
  ];

  const groupData = [
    {
      name: "None",
      value: "NULL",
    },
    {
      name: "Date",
      value: "Date",
    },
    {
      name: " Fee Earner",
      value: "FeeEarnerRef",
    },
    {
      name: "Matter Ref",
      value: "MatterRef",
    },
  ];

  const orderData = [
    {
      name: "Fee Earner",
      value: "FeeEarnerRef",
    },
    {
      name: "Date",
      value: "Date",
    },
    {
      name: "Reference",
      value: "Reference",
    },
    {
      name: "Matter Ref",
      value: "MatterRef",
    },
  ];

  const DEFAULT_GROUP_BY = {
    name: "None",
    value: "NULL",
  };

  const DEFAULT_ORDER_BY = {
    name: "Date",
    value: "Date",
  };

  let date = new Date();
  const [fromDate, setFromDate] = React.useState(new Date(date.getFullYear(), date.getMonth(), 1));
  const [reportType, setReportType] = useState("BillsDayBook.trdp");

  const analyzeParameters = () => {
    setParameters({
      ExcludePaidBills: excludeZero,
      FromDate: includeFromDate ? formatDateForAPI(fromDate) : undefined,
      ToDate: includeEndDate ? formatDateForAPI(endDate) : undefined,
      OrderBy: orderbyType?.value ?? DEFAULT_ORDER_BY.value,
      GroupBy: groupByType?.value ?? DEFAULT_GROUP_BY.value,
      IncludeReversals: includeReversals,
      DepartmentId: selectedDepartment?.departmentID ?? null,
      WorkTypeId: selectedWorkType?.workTypeID ?? null,
      FeeEarnerRef:
        selectedFeeEarners.length > 0
            ? selectedFeeEarners
                .map((feeEarner) => feeEarner.userRef)
                .join(",")
            : null,
    });
    setShouldLoad(true);
  };

  const [orderbyType, setOrderByType] = useState(DEFAULT_ORDER_BY);

  const [groupByType, setGroupByType] = useState(DEFAULT_GROUP_BY);

  const [includeFromDate, setIncludeFromDate] = useState(false);
  const [includeEndDate, setIncludeEndDate] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const { data: company } = useCompany();
  const { data: departmentData } = useActiveDepartments();
  const { data: allActiveWorkTypes } = useActiveWorkTypes();
  const { data: feeEarnerData } = useActiveFeeEarner();
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedWorkType, setSelectedWorkType] = useState(null);
  const [selectedFeeEarners, setSelectedFeeEarners] = useState([]);

  const workTypeOptions = useMemo(() => {
    if (!selectedDepartment?.departmentID) return [];
    return (allActiveWorkTypes || []).filter(
      (workType) => workType.departmentId === selectedDepartment.departmentID,
    );
  }, [selectedDepartment?.departmentID, allActiveWorkTypes]);

  const feeEarnerOptions = useMemo(
    () =>
      (feeEarnerData || []).map((user) => ({
        ...user,
        display: `${user.userRef} - ${user.displayname}`,
      })),
    [feeEarnerData],
  );

  const presets = [
    { label: "This Month", value: "ThisMonth" },
    { label: "This Quarter", value: "ThisQuarter" },
    { label: "This Year", value: "ThisYear" },
  ];

  const applyPreset = (preset) => {
    const today = new Date();
    let newFromDate, newToDate;
    if (preset === "ThisMonth") {
      newFromDate = new Date(today.getFullYear(), today.getMonth(), 1);
      newToDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (preset === "ThisQuarter") {
      const startMonth = today.getMonth() - (today.getMonth() % 3);
      newFromDate = new Date(today.getFullYear(), startMonth, 1);
      newToDate = new Date(today.getFullYear(), startMonth + 3, 0);
    } else if (preset === "ThisYear") {
      if (company?.yearStartDate) newFromDate = new Date(company.yearStartDate);
      if (company?.yearEndDate) newToDate = new Date(company.yearEndDate);
    }
    setFromDate(newFromDate);
    setEndDate(newToDate);
    setIncludeFromDate(true);
    setIncludeEndDate(true);
    setSelectedPreset(preset);
  };

  const [excludeZero, setExcludeZero] = useState(false);
  const [includeReversals, setIncludeReversals] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);

  const [endDate, setEndDate] = React.useState(date);

  const [parameters, setParameters] = useState({
    ExcludePaidBills: excludeZero,
    FromDate: includeFromDate ? formatDateForAPI(fromDate) : undefined,
    ToDate: includeEndDate ? formatDateForAPI(endDate) : undefined,
    OrderBy: "Date",
    GroupBy: "NULL",
    IncludeReversals: includeReversals,
    DepartmentId: null,
    WorkTypeId: null,
    FeeEarnerRef: [],
  });

  const UpdateButton = (
    <Button className="common-btn w-100" onClick={analyzeParameters}>
      Update
    </Button>
  );

  const dateRangeSection = (
    <div>
      <p className={`${styles.heading} mb-2`} style={{ fontSize: "13px" }}>Date Range</p>
      <div className={styles.dateRangeCards}>
        <div className={styles.dateCard}>
          <Checkbox
            value={includeFromDate}
            onChange={(e) => {
              setIncludeFromDate(e.value);
              if (!e.value) setSelectedPreset(null);
            }}
            label=""
          />
          <div >
            <span className={styles.dateCardLabel}>From</span>
            <DatePicker
              value={fromDate || null}
              format="dd/MM/yyyy"
              onChange={(e) => {
                setFromDate(e.value);
                setIncludeFromDate(true);
                setSelectedPreset(null);
              }}
              toggleButton={CalenderCustomToggleButton}
              className={styles.dateCardPicker}
              width="100%"
            />
          </div>
        </div>
        <div className={styles.dateCard}>
          <Checkbox
            value={includeEndDate}
            onChange={(e) => {
              setIncludeEndDate(e.value);
              if (!e.value) setSelectedPreset(null);
            }}
            label=""
          />
          <div >
            <span className={styles.dateCardLabel}>To</span>
            <DatePicker
              value={endDate || null}
              format="dd/MM/yyyy"
              onChange={(e) => {
                setEndDate(e.value);
                setIncludeEndDate(true);
                setSelectedPreset(null);
              }}
              toggleButton={CalenderCustomToggleButton}
              className={styles.dateCardPicker}
              width="100%"
            />
          </div>
        </div>
      </div>
      <div className={styles.datePresets}>
        {presets.map((p) => (
          <button
            key={p.value}
            type="button"
            className={`${styles.presetBtn} ${selectedPreset === p.value ? styles.presetBtnActive : ""}`}
            onClick={() => applyPreset(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );

  const radioGroup = (
    <div className={`${styles.typeTabs} ${styles.typeTabsGrid}`}>
      {reportsData.map((item) => (
        <button
          key={item.value}
          type="button"
          className={`${styles.typeTab} ${reportType === item.value ? styles.typeTabActive : ""}`}
          onClick={() => setReportType(item.value)}
        >
          <span
            className={styles.typeTabIcon}
            style={{
              WebkitMaskImage: `url(${item.icon})`,
              maskImage: `url(${item.icon})`,
            }}
            aria-hidden="true"
          />
          <span className="fw-semibold text-nowrap lh-sm" style={{ fontSize: "10.5px" }}>{item.label}</span>
        </button>
      ))}
    </div>
  );

  const IncludeCheckbox = (
    <Checkbox
      className="checkbox-label"
      value={excludeZero}
      onChange={(e) => {
        setExcludeZero(e.value);
      }}
      style={{
        fontSize: "12px",
      }}
      label={"Exclude Paid Bills"}
    />
  );

  const IncludeReversals = (
    <Checkbox
      className="checkbox-label"
      value={includeReversals}
      onChange={(e) => {
        setIncludeReversals(e.value);
      }}
      style={{
        fontSize: "12px",
      }}
      label={"Include Reversals"}
    />
  );

  const orderByDropDown = (
    <div className="">
      <ComboBox
        className="ps-1"
        iconClassName="wa"
        icon={<Icon icon="arrow-down" />}
        data={orderData}
        required
        value={orderbyType}
        disabled={(groupByType?.value ?? DEFAULT_GROUP_BY.value) !== "NULL"}
        onChange={(e) => {
          setOrderByType(e.value ?? DEFAULT_ORDER_BY);
        }}
        dataItemKey={"value"}
        textField={"name"}
        placeholder="Order by"
        style={{ width: "100%", maxWidth: "325px", height: "44px" }}
      />
    </div>
  );

  const groupByDropDown = (
    <div className="">
      <ComboBox
        className="ps-1"
        iconClassName="wa"
        icon={<Icon icon="arrow-down" />}
        data={groupData}
        required
        value={groupByType}
        onChange={(e) => {
          setGroupByType(e.value ?? DEFAULT_GROUP_BY);
        }}
        dataItemKey={"value"}
        textField={"name"}
        placeholder="Group by"
        style={{ width: "100%", maxWidth: "325px", height: "44px" }}
      />
    </div>
  );

  const departmentDropDown = (
    <ComboBox
      className="ps-1"
      iconClassName="wa"
      icon={<Icon icon="arrow-down" />}
      data={departmentData || []}
      value={selectedDepartment}
      onChange={(e) => {
        setSelectedDepartment(e.value);
        setSelectedWorkType(null);
      }}
      dataItemKey="departmentID"
      textField="name"
      placeholder="Select Department"
      style={{ width: "100%", maxWidth: "325px", height: "44px" }}
    />
  );

  const workTypeDropDown = (
    <ComboBox
      className="ps-1"
      iconClassName="wa"
      icon={<Icon icon="arrow-down" />}
      data={workTypeOptions}
      value={selectedWorkType}
      onChange={(e) => setSelectedWorkType(e.value)}
      dataItemKey="workTypeID"
      textField="name"
      placeholder="Select Work Type"
      disabled={!selectedDepartment}
      style={{ width: "100%", maxWidth: "325px", height: "44px" }}
    />
  );

  const feeEarnerDropDown = (
    <MultiSelect
      className="ps-1"
      data={feeEarnerOptions}
      value={selectedFeeEarners}
      onChange={(e) => setSelectedFeeEarners(e.value)}
      dataItemKey="userRef"
      textField="display"
      placeholder="Select Fee Earner"
      style={{ width: "100%", maxWidth: "325px", minHeight: "44px" }}
    />
  );

  const reportViewer = (
    <ReportViewer
      name={reportType}
      parameters={parameters}
      shouldLoad={shouldLoad}
      setShouldLoad={setShouldLoad}
      modalHeight={"81vh"}
      url={`${ReportURL}/Reporting/reports`}
    />
  );

  return (
    <div className="container-fluid w-100 p-4 pt-2 mt-4">
      <div className="d-flex gap-4">
        <div className={styles.filterSidebar}>
          <div
            className={`p-4 pt-4 ${styles.reportBackground} ${styles['height']}`}
          >
            <div className="mt-1">{radioGroup}</div>
            <SectionDividerFull sectionName={""} />
            <div className="mt-2">{dateRangeSection}</div>
            <SectionDividerFull sectionName={""} />
            <div className="d-flex flex-column pt-1">
              <Label className="label mb-1">Department</Label>
              {departmentDropDown}
            </div>
            <div className="d-flex flex-column pt-2">
              <Label className="label mb-1">Work Type</Label>
              {workTypeDropDown}
            </div>
            <div className="d-flex flex-column pt-2">
              <Label className="label mb-1">Fee Earner</Label>
              {feeEarnerDropDown}
            </div>
            <SectionDividerFull sectionName={""} />
            <div className="d-flex flex-column pt-1">
              <Label className="label mb-1">Order By</Label>
              {orderByDropDown}
            </div>
            <div className="d-flex flex-column pt-2">
              <Label className="label mb-1">Group By</Label>
              {groupByDropDown}
            </div>
            <SectionDividerFull sectionName={""} />
            <div className={`${styles["checkbox-label"]} pt-3`}>{IncludeCheckbox}</div>
            <div className={`${styles["checkbox-label"]} pt-1`}>{IncludeReversals}</div>
            <SectionDividerFull sectionName={""} />
            <div className="pt-4">{UpdateButton}</div>
          </div>
        </div>
        <div className={styles.reportViewerCol}>
          <div className={`row ${styles.reportBackground} 1fr`}>
            {reportViewer}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingReport;
